import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Theme,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/styles";
import classNames from "classnames";
import { parseISO } from "date-fns";
import { Record, useTranslate } from "ra-core";
import React, { CSSProperties, memo } from "react";
import { NumberField } from "react-admin";
import { shallowEqual, useSelector } from "react-redux";
import {
  DriverFmcsaInspectionDto,
  EventViewDto,
} from "../../../../common/data/service-proxies";
import { AppState } from "../../../../common/types";
import ColoredStatusField from "../../../common/data-grid-fields/ColoredStatusField";
import TimeField from "../../../common/data-grid-fields/TimeField";
import DeleteIcon from "@material-ui/icons/Delete";
import { getBackgroundColor, getColor } from "./eventUtils";
import { Edit, SyncAlt } from "@material-ui/icons";
import { isTimeInRange } from "../../../../common/util/isTimeInRange";

interface Props {
  event: EventViewDto;
  homeTerminalTimeZone: string;
  onDelete: (event: EventViewDto) => void;
  onResize: (event: EventViewDto) => void;
  className?: string;
  hovered?: boolean;
  onClick?: React.MouseEventHandler<HTMLTableRowElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLTableRowElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLTableRowElement>;
  onRowSelected?: (event: EventViewDto, selected: boolean) => void;
  style?: CSSProperties;
  canDelete?: boolean;
  canResize?: boolean;
  odd?: boolean;
  selected?: boolean;
  index: number;
  fmcsaInspection?: DriverFmcsaInspectionDto | null;
  gridColumnsSetFilter: string[];
}

const getClassName = (
  hovered: boolean | undefined,
  className: string,
  themeMode: string
) => {
  return hovered
    ? classNames(className, `hos-event-highlight-hovered-${themeMode}`)
    : className;
};

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    "&:hover": {
      backgroundColor: theme.palette.action.focus,
    },
  },
  odd: {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const HosDriverLogGridEvent = memo(
  (props: Props) => {
    const themeMode = useSelector((x: AppState) => x.theme);
    const theme: Theme = useTheme();
    const translate = useTranslate();

    const {
      event,
      homeTerminalTimeZone,
      onDelete,
      onResize,
      className,
      hovered,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onRowSelected,
      style,
      canDelete,
      canResize,
      odd,
      selected,
      index,
      fmcsaInspection,
      gridColumnsSetFilter,
    } = props;

    const isEditable =
      !isTimeInRange(
        event.startTime!,
        fmcsaInspection?.reportTimeFrom,
        fmcsaInspection?.reportTimeTo
      ) && !event.isFirstEvent;

    const classes = useStyles();

    const handleRowSelected = (
      event: React.MouseEvent<HTMLButtonElement>,
      rowData: EventViewDto
    ) => {
      if (onRowSelected) {
        onRowSelected(rowData, (event.target as HTMLInputElement).checked);
      }

      event.stopPropagation();
    };

    return (
      <TableRow
        component="div"
        className={`${getClassName(hovered, className ?? "", themeMode)} ${
          odd ? classes.odd : ""
        } ${hovered ? classes.root : ""}`}
        onMouseEnter={(e: any) => {
          if (onMouseEnter) onMouseEnter({ ...e, detail: event.id! });
        }}
        onMouseLeave={onMouseLeave}
        style={style}>
        {canDelete ? (
          <TableCell
            component="div"
            className={classNames(
              "checkbox-cell",
              !isEditable ? "not-editable" : ""
            )}>
            <Checkbox
              checked={selected && isEditable}
              onClick={(e) =>
                isEditable ? handleRowSelected(e, event) : undefined
              }
            />
          </TableCell>
        ) : null}
        <TableCell component="div" className="row-counter">
          {index}
        </TableCell>
        {gridColumnsSetFilter.includes("status") ? (
          <TableCell component="div" className="status">
            <ColoredStatusField
              record={event}
              color={(record, source, _) => getColor(record, source, theme)}
              backgroundColor={(record, source, _) =>
                getBackgroundColor(record, source, theme)
              }
              applyColorTo="everything"
              labelStyle="columns"
              source="dutyStatus"
              labelTranslationNamespace="appData.dutyCycles"
            />
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("start") ? (
          <TableCell component="div" className="start">
            {parseISO(event.startTime!).toLocaleTimeString("en-US", {
              timeZone: homeTerminalTimeZone,
            })}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("duration") ? (
          <TableCell component="div" className="duration">
            <TimeField
              record={event as Record}
              source="durationInSeconds"
              inputType="seconds"
              outputType="long"
              emptyDisplay="-"
            />
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("location") ? (
          <TableCell component="div" className="location">
            {event.locationDisplayName}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("vehicle") ? (
          <TableCell component="div" className="vehicle">
            {event.vehicleName}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("odometer") ? (
          <TableCell component="div" className="odometer">
            {event.id && (
              <NumberField
                record={{
                  ...event,
                  id: event.id,
                  odometer: !!event.odometer ? event.odometer : "-",
                }}
                source="odometer"
              />
            )}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("co-driver") ? (
          <TableCell component="div" className="co-driver">
            {event.coDriverDisplayName}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("engine-hours") ? (
          <TableCell component="div" className="engine-hours">
            {event.id && (
              <NumberField
                record={{ ...event, id: event.id }}
                source="engineMinutes"
              />
            )}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("trailers") ? (
          <TableCell component="div" className="trailers">
            {event.attachedTrailers}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("origin") ? (
          <TableCell component="div" className="origin">
            {event.origin &&
              translate(
                `resources.hos-driver-log-view.logSources.${event.origin}`
              )}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("shipping-docs") ? (
          <TableCell component="div" className="shipping-docs">
            {event.shippingDocuments}
          </TableCell>
        ) : null}
        {gridColumnsSetFilter.includes("notes") ? (
          <TableCell component="div" className="notes">
            {event.notes}
          </TableCell>
        ) : null}
        <TableCell
          component="div"
          className={classNames(
            canDelete ? "action-buttons-with-delete" : "action-buttons",
            !isEditable ? "not-editable" : ""
          )}>
          {canResize ? (
            <IconButton
              style={{
                color: canResize
                  ? theme.palette.primary.main
                  : theme.palette.action.disabled,
              }}
              onClick={(e) => {
                onResize(event);
                e.stopPropagation();
              }}>
              <SyncAlt />
            </IconButton>
          ) : null}
          <IconButton
            style={{ color: theme.palette.primary.main }}
            onClick={(e: any) =>
              onClick && onClick({ ...e, detail: event.id! })
            }>
            <Edit />
          </IconButton>
          {canDelete ? (
            <IconButton
              style={{ color: theme.palette.error.main }}
              onClick={(e) => {
                onDelete(event);
                e.stopPropagation();
              }}>
              <DeleteIcon />
            </IconButton>
          ) : null}
        </TableCell>
      </TableRow>
    );
  },
  (prev, next) => shallowEqual(prev, next)
);
